import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaCommentDots,
  FaHandsHelping,
  FaInfoCircle,
  FaLock,
  FaMinusCircle,
  FaPlusCircle,
} from 'react-icons/fa';
import programmeService from '../services/programmeService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/blocks/LoadingSpinner';
import MobileProgrammeSwipeAction from '../components/widgets/MobileProgrammeSwipeAction';
import DSEScoreInputer from '../components/widgets/DSEScoreInputer';
import JupasSurveySyncModal from '../components/widgets/JupasSurveySyncModal';
import JupasSurveyAdmittedConfirmModal from '../components/widgets/JupasSurveyAdmittedConfirmModal';
import JupasSurveyCandidateConfirmModal from '../components/widgets/JupasSurveyCandidateConfirmModal';
import { getSubjectShortLabel } from '../utils/dseSubjects';
import { extractInstitutionAliasQuery, getInstitutionShortLabel } from '../utils/institutionAssets';
import { JUPAS_HIGH_RISK_ACTION_CLASS, JUPAS_VERY_SAFE_ACTION_CLASS } from '../utils/jupasClassificationDisplay';
import { JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS, JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS } from '../utils/jupasLayoutDisplay';
import { getJupasOrderBandColor, getJupasOrderRankLabel } from '../utils/jupasOrderDisplay';
import { getProgrammePrimaryTitle, getProgrammeRankingBarTitle } from '../utils/programmeDisplay';
import {
  buildJupasSurveyPayload,
  getHighestAccessibleSurveyStep,
  getSurveyStepAccess,
  INTEREST_LOW_HELP_REASONS,
  requiresInterestLowHelpReason,
  validateJupasSurveySubmission,
} from '../utils/jupasSurveySubmitPayload';
import apiClient from '../utils/apiClient';
import {
  detectScoreConflicts,
  fetchAccountProgrammeOrder,
  fetchAccountScoreMap,
  mapToScoreRows,
  mergeCommentFromUser,
  mergeScoreMaps,
  resolveRankingProgrammes,
  saveAccountProgrammeOrder,
  saveAccountScoreMap,
  scoreRowsToMap,
  shouldFillRankingFromAccount,
} from '../utils/jupasSurveyAccountSync';
import {
  hasJupasSurveyCandidateConfirmation,
  setJupasSurveyCandidateConfirmed,
} from '../utils/jupasSurveyCandidateGate';
import { resolveProgrammeListingCollection } from '../utils/resolveProgrammeListingCollection';

const STORAGE_KEY = 'edvise:jupas-survey-ranking:v1';
const SURVEY_DRAFT_KEY = 'edvise:jupas-survey-draft:v1';
const MAX_PROGRAMMES = 20;
const PAGE_SIZE = 10;
const FEEDBACK_SCALE = [1, 2, 3, 4, 5];
const INITIAL_SURVEY_EXTRAS = {
  categoryBCSubject: '',
  categoryBCScore: '',
  categoryBCActive: false,
  spnBonusScheme: '',
  spnBonusScore: '',
  spnBonusActive: false,
};
const INITIAL_COMMENT_FORM = {
  usedCalculator: '',
  calculatorHelpfulness: '',
  usedInterestTest: '',
  interestHelpfulness: '',
  interestLowHelpReason: '',
  interestLowHelpReasonOther: '',
  interestAccuracy: '',
  improvements: '',
  freeformFeedback: '',
  volunteerWilling: false,
  volunteerName: '',
  volunteerContactMethod: '',
  volunteerContact: '',
};

const compareProgrammeCodes = (a, b) =>
  String(a?.code ?? '').localeCompare(String(b?.code ?? ''));

const getLatestOfferTotal = (programme) => Number(programme?.stats_offer?.[0]?.[6] || 0);

function readStoredRanking() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item?.code).slice(0, MAX_PROGRAMMES) : [];
  } catch (_error) {
    return [];
  }
}

function readSurveyDraft() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SURVEY_DRAFT_KEY) || '{}');
    const rawScores = Array.isArray(parsed.scores) ? parsed.scores : [];
    return {
      scores: mapToScoreRows(scoreRowsToMap(rawScores)),
      extras: { ...INITIAL_SURVEY_EXTRAS, ...(parsed.extras || {}) },
      comment: { ...INITIAL_COMMENT_FORM, ...(parsed.comment || {}) },
    };
  } catch (_error) {
    return {
      scores: [],
      extras: { ...INITIAL_SURVEY_EXTRAS },
      comment: { ...INITIAL_COMMENT_FORM },
    };
  }
}

function writeSurveyDraft(partial) {
  const current = readSurveyDraft();
  window.localStorage.setItem(SURVEY_DRAFT_KEY, JSON.stringify({ ...current, ...partial }));
}

function clearSurveyStorage() {
  window.localStorage.removeItem(SURVEY_DRAFT_KEY);
  window.localStorage.removeItem(STORAGE_KEY);
}

function ProgrammeIdentity({
  programme,
  language,
  admittedLabel = null,
  admittedLabelLayout = 'inline',
  preferShortEnglishName = false,
}) {
  const admittedBadgeClass = admittedLabelLayout === 'stacked'
    ? 'flex shrink-0 items-center justify-center self-stretch rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-bold leading-tight text-white'
    : 'ml-auto shrink-0 rounded-full bg-emerald-600 px-1.5 py-px text-[8px] font-bold leading-tight text-white';
  const programmeTitle = preferShortEnglishName
    ? getProgrammeRankingBarTitle(programme, language)
    : getProgrammePrimaryTitle(programme, language);

  const programmeMeta = (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="shrink-0 text-[9px] font-bold leading-none text-link">
          {programme.code}
        </span>
        <span className="min-w-0 truncate text-[9px] font-medium text-ink-subtle">
          {getInstitutionShortLabel(programme.institution, language)}
        </span>
        {admittedLabel && admittedLabelLayout === 'inline' ? (
          <span className={admittedBadgeClass}>
            {admittedLabel}
          </span>
        ) : null}
      </div>
      <p className="mt-px truncate text-[11px] font-medium leading-tight text-ink">
        {programmeTitle}
      </p>
    </>
  );

  if (admittedLabel && admittedLabelLayout === 'stacked') {
    return (
      <div className="flex min-w-0 flex-1 select-none items-stretch gap-2 text-left">
        <div className="min-w-0 flex-1">{programmeMeta}</div>
        <span className={admittedBadgeClass}>
          {admittedLabel}
        </span>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1 select-none text-left">
      {programmeMeta}
    </div>
  );
}

function ListPager({ page, totalPages, onPageChange, label, t }) {
  return (
    <nav
      aria-label={label}
      className="flex h-7 shrink-0 items-center justify-center gap-1.5 border-t border-edge px-2"
    >
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="flex h-5 w-5 items-center justify-center rounded text-ink-muted transition hover:bg-surface-muted hover:text-link disabled:opacity-25"
        aria-label={t('jupasListSubmission.previousPage')}
      >
        <FaChevronLeft className="h-2.5 w-2.5" aria-hidden />
      </button>
      <span className="min-w-10 text-center text-[9px] font-medium tabular-nums text-ink-subtle">
        {page}/{totalPages}
      </span>
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="flex h-5 w-5 items-center justify-center rounded text-ink-muted transition hover:bg-surface-muted hover:text-link disabled:opacity-25"
        aria-label={t('jupasListSubmission.nextPage')}
      >
        <FaChevronRight className="h-2.5 w-2.5" aria-hidden />
      </button>
    </nav>
  );
}

const SURVEY_FLOW_STEPS = ['score', 'ranking', 'comment', 'review'];
const SURVEY_STEP_LABEL_KEYS = {
  score: 'jupasListSubmission.stepScore',
  ranking: 'jupasListSubmission.stepRanking',
  comment: 'jupasListSubmission.stepComment',
  review: 'jupasListSubmission.stepReview',
};

function SurveyStepFooter({
  activeStep,
  onStepChange,
  onSubmit,
  onBackHome,
  submitDisabled = false,
  stepAccess,
  t,
}) {
  const stepIndex = SURVEY_FLOW_STEPS.indexOf(activeStep);
  const hasPrevious = stepIndex > 0;
  const hasNext = stepIndex >= 0 && stepIndex < SURVEY_FLOW_STEPS.length - 1;
  const previousStepId = hasPrevious ? SURVEY_FLOW_STEPS[stepIndex - 1] : null;
  const nextStepId = hasNext ? SURVEY_FLOW_STEPS[stepIndex + 1] : null;
  const canAdvance = nextStepId ? stepAccess[nextStepId] : false;
  const nextLockedHint = nextStepId
    ? t(`jupasListSubmission.unlock.${nextStepId}`)
    : '';
  const pillBaseClass = 'inline-flex h-9 min-w-[6.5rem] items-center justify-center gap-1.5 rounded-full px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40';

  if (activeStep === 'review') {
    return (
      <nav
        aria-label={t('jupasListSubmission.stepNavigation')}
        className="mt-3 flex items-center justify-between gap-2"
      >
        <button
          type="button"
          onClick={() => onStepChange(SURVEY_FLOW_STEPS[stepIndex - 1])}
          className={`${pillBaseClass} border border-edge bg-surface text-ink-muted hover:border-edge-strong hover:bg-surface-muted hover:text-ink`}
        >
          <FaChevronLeft className="h-3 w-3 shrink-0" aria-hidden />
          {t('jupasListSubmission.backToStep', { step: t(SURVEY_STEP_LABEL_KEYS.comment) })}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitDisabled}
          className={`${pillBaseClass} bg-primary text-primary-foreground shadow-sm hover:bg-primary/90`}
        >
          {submitDisabled ? t('jupasListSubmission.submit.submitting') : t('jupasListSubmission.submit.submitButton')}
          <FaChevronRight className="h-3 w-3 shrink-0" aria-hidden />
        </button>
      </nav>
    );
  }

  return (
    <nav
      aria-label={t('jupasListSubmission.stepNavigation')}
      className="mt-3 flex items-center justify-between gap-2"
    >
        <button
          type="button"
          onClick={() => {
            if (activeStep === 'score') {
              onBackHome?.();
              return;
            }
            onStepChange(SURVEY_FLOW_STEPS[stepIndex - 1]);
          }}
          disabled={!hasPrevious && activeStep !== 'score'}
          className={`${pillBaseClass} border border-edge bg-surface text-ink-muted hover:border-edge-strong hover:bg-surface-muted hover:text-ink`}
        >
          <FaChevronLeft className="h-3 w-3 shrink-0" aria-hidden />
          {activeStep === 'score'
            ? t('jupasListSubmission.backToHome')
            : previousStepId
              ? t('jupasListSubmission.backToStep', { step: t(SURVEY_STEP_LABEL_KEYS[previousStepId]) })
              : t('jupasListSubmission.previousPage')}
        </button>
        <button
          type="button"
          onClick={() => onStepChange(SURVEY_FLOW_STEPS[stepIndex + 1])}
          disabled={!hasNext || !canAdvance}
          title={!canAdvance && nextLockedHint ? nextLockedHint : undefined}
          className={`${pillBaseClass} bg-primary text-primary-foreground shadow-sm hover:bg-primary/90`}
        >
          {nextStepId
            ? t('jupasListSubmission.goToStep', { step: t(SURVEY_STEP_LABEL_KEYS[nextStepId]) })
            : t('jupasListSubmission.nextPage')}
        <FaChevronRight className="h-3 w-3 shrink-0" aria-hidden />
      </button>
    </nav>
  );
}

function SurveyStepNavigation({ activeStep, onStepChange, stepAccess, t }) {
  const steps = [
    { id: 'score', label: t('jupasListSubmission.stepScore') },
    { id: 'ranking', label: t('jupasListSubmission.stepRanking') },
    { id: 'comment', label: t('jupasListSubmission.stepComment') },
    { id: 'review', label: t('jupasListSubmission.stepReview') },
  ];
  const activeIndex = Math.max(0, steps.findIndex((step) => step.id === activeStep));

  return (
    <nav
      aria-label={t('jupasListSubmission.stepsLabel')}
      className="mb-3 rounded-full border border-edge bg-surface p-0.5 shadow-sm"
    >
      <div className="relative">
        <span
          className="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-primary transition-transform duration-500 ease-[cubic-bezier(0.34,1.45,0.64,1)] motion-reduce:transition-none"
          style={{
            width: 'calc((100% - 0.75rem) / 4)',
            transform: `translateX(calc(${activeIndex * 100}% + ${activeIndex * 0.25}rem))`,
          }}
          aria-hidden
        />
        <ol className="relative z-10 grid grid-cols-4 gap-1">
          {steps.map((step, index) => {
            const active = activeStep === step.id;
            const locked = !stepAccess[step.id];
            const lockedHint = locked ? t(`jupasListSubmission.unlock.${step.id}`) : '';
            return (
              <li key={step.id} className="min-w-0">
                <button
                  type="button"
                  onClick={() => onStepChange(step.id)}
                  disabled={locked}
                  title={lockedHint || undefined}
                  aria-current={active ? 'step' : undefined}
                  aria-disabled={locked || undefined}
                  className={`flex h-8 w-full min-w-0 items-center justify-center gap-1 rounded-full px-1 text-sm font-semibold transition-all duration-300 ${
                    active
                      ? 'text-white'
                      : locked
                        ? 'cursor-not-allowed text-ink-subtle opacity-40'
                        : 'text-ink-muted hover:bg-surface-muted hover:text-ink'
                  }`}
                >
                  <span
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-[8px] font-bold transition-colors duration-300 ${
                      active ? 'bg-white/20 text-white' : locked ? 'bg-edge/80 text-ink-subtle' : 'bg-edge text-ink-muted'
                    }`}
                    aria-hidden
                  >
                    {locked ? <FaLock className="h-2 w-2" /> : index + 1}
                  </span>
                  <span className="truncate">{step.label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

function CommentFieldLabel({ children, required = false }) {
  return (
    <p className="text-base font-semibold leading-relaxed text-ink">
      {children}
      {required ? <span className="ml-0.5 text-red-500">*</span> : null}
    </p>
  );
}

function YesNoPills({ value, onChange, t }) {
  return (
    <div className="mt-1.5 grid grid-cols-2 gap-1 rounded-lg bg-surface-muted p-1">
      {[
        { value: 'yes', label: t('jupasListSubmission.comment.yes') },
        { value: 'no', label: t('jupasListSubmission.comment.no') },
      ].map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
            className={`flex h-8 items-center justify-center rounded-md text-base font-semibold transition-all ${
              selected
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-ink-muted hover:bg-surface hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function FeedbackScale({ value, onChange, lowLabel, highLabel, ariaLabel }) {
  return (
    <div className="mt-1.5">
      <div className="grid grid-cols-5 gap-1" role="group" aria-label={ariaLabel}>
        {FEEDBACK_SCALE.map((score) => {
          const selected = String(value) === String(score);
          return (
            <button
              key={score}
              type="button"
              onClick={() => onChange(score)}
              aria-pressed={selected}
              className={`flex h-9 items-center justify-center rounded-lg border text-base font-bold tabular-nums transition-all ${
                selected
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-edge bg-surface text-ink-muted hover:border-edge-strong hover:bg-surface-muted'
              }`}
            >
              {score}
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between gap-3 text-base leading-tight text-ink-subtle">
        <span>{lowLabel}</span>
        <span className="text-right">{highLabel}</span>
      </div>
    </div>
  );
}

function InterestLowHelpReasonSelector({ value, otherValue, onChange, onOtherChange, t }) {
  return (
    <div className="mt-2.5 rounded-xl border border-amber-200/80 bg-amber-50/70 p-2.5 dark:border-amber-900/40 dark:bg-amber-950/25">
      <CommentFieldLabel required>{t('jupasListSubmission.comment.interestLowHelpReason')}</CommentFieldLabel>
      <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
        {t('jupasListSubmission.comment.interestLowHelpReasonHint')}
      </p>
      <div className="mt-2 space-y-1.5" role="radiogroup" aria-label={t('jupasListSubmission.comment.interestLowHelpReason')}>
        {INTEREST_LOW_HELP_REASONS.map((reason) => {
          const selected = value === reason;
          return (
            <div key={reason}>
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onChange(reason)}
                className={`flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition ${
                  selected
                    ? 'border-primary bg-primary/10 text-ink shadow-sm'
                    : 'border-edge bg-surface text-ink-muted hover:border-edge-strong hover:bg-surface-muted hover:text-ink'
                }`}
              >
                <span
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    selected ? 'border-primary bg-primary text-primary-foreground' : 'border-edge-strong bg-surface'
                  }`}
                  aria-hidden
                >
                  {selected ? <FaCheck className="h-2 w-2" /> : null}
                </span>
                <span className="text-sm font-medium leading-relaxed">
                  {t(`jupasListSubmission.comment.interestLowHelpReasons.${reason}`)}
                </span>
              </button>
              {reason === 'other' && selected ? (
                <label className="mt-2 block pl-1">
                  <span className="sr-only">{t('jupasListSubmission.comment.interestLowHelpReasonOther')}</span>
                  <textarea
                    value={otherValue}
                    onChange={(event) => onOtherChange(event.target.value)}
                    className="min-h-20 w-full resize-y rounded-lg border border-edge-strong bg-surface px-3 py-2 text-sm font-normal leading-relaxed text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring"
                    placeholder={t('jupasListSubmission.comment.interestLowHelpReasonOtherPlaceholder')}
                    maxLength={500}
                    rows={3}
                  />
                </label>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FlowSwipeHint({ direction = 'right', t }) {
  const label = direction === 'right'
    ? t('jupasListSubmission.swipeHintRight')
    : t('jupasListSubmission.swipeHintLeft');
  const animationClass = direction === 'right'
    ? 'jupas-flow-chevron-right'
    : 'jupas-flow-chevron-left';

  return (
    <span
      className={`${animationClass} inline-flex shrink-0 items-center text-[10px] font-semibold leading-none sm:text-[11px] ${
        direction === 'left' ? 'text-red-600' : 'text-emerald-600'
      }`}
      aria-hidden
    >
      {label}
    </span>
  );
}

const INSTRUCTION_TONE_STYLES = {
  amber: {
    box: 'border-amber-200/80 bg-amber-50/90 dark:border-amber-900/40 dark:bg-amber-950/30',
    icon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    title: 'text-amber-950 dark:text-amber-100',
    body: 'text-amber-900/80 dark:text-amber-200/80',
    bullet: 'text-amber-700/70 dark:text-amber-300/70',
  },
  blue: {
    box: 'border-sky-200/80 bg-sky-50/90 dark:border-sky-900/40 dark:bg-sky-950/30',
    icon: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    title: 'text-sky-950 dark:text-sky-100',
    body: 'text-sky-900/80 dark:text-sky-200/80',
    bullet: 'text-sky-700/70 dark:text-sky-300/70',
  },
};

function InstructionNotice({ icon: Icon, title, titleId, children, className = 'mb-3', tone = 'amber' }) {
  const styles = INSTRUCTION_TONE_STYLES[tone] || INSTRUCTION_TONE_STYLES.amber;

  return (
    <aside
      aria-labelledby={titleId}
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 shadow-sm ${styles.box} ${className}`}
    >
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-full ${styles.icon}`}>
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <h2 id={titleId} className={`text-sm font-bold ${styles.title}`}>{title}</h2>
        {children}
      </div>
    </aside>
  );
}

function InstructionBulletText({ i18nKey }) {
  return (
    <Trans
      i18nKey={i18nKey}
      components={{ strong: <span className="text-link" /> }}
    />
  );
}

function StepInstructions({ stepKey, t }) {
  const prefix = `jupasListSubmission.instructions.${stepKey}`;
  const styles = INSTRUCTION_TONE_STYLES.blue;

  const renderBullet = (content, key) => (
    <li key={key} className="flex items-start gap-1.5">
      <span className={`mt-0.5 shrink-0 leading-none ${styles.bullet}`} aria-hidden>•</span>
      <span>{content}</span>
    </li>
  );

  const scoreBulletKeys = [1, 2, 3]
    .map((index) => `${prefix}.bullet${index}`)
    .filter((i18nKey) => t(i18nKey, { defaultValue: '' }));

  return (
    <InstructionNotice
      icon={FaInfoCircle}
      title={t(`${prefix}.title`)}
      titleId={`jupas-survey-${stepKey}-instructions-title`}
      tone="blue"
    >
      <ul className={`mt-1 space-y-1 text-xs leading-relaxed ${styles.body}`}>
        {stepKey === 'ranking' ? (
          <>
            {renderBullet(<InstructionBulletText i18nKey={`${prefix}.bullet1`} />, 'ranking-1')}
            {renderBullet(
              <>
                <span className="hidden md:inline">
                  <InstructionBulletText i18nKey={`${prefix}.bullet2Desktop`} />
                </span>
                <span className="md:hidden">
                  <InstructionBulletText i18nKey={`${prefix}.bullet2Mobile`} />
                </span>
              </>,
              'ranking-2',
            )}
            {renderBullet(<InstructionBulletText i18nKey={`${prefix}.bullet3`} />, 'ranking-3')}
          </>
        ) : (
          scoreBulletKeys.map((i18nKey, index) => (
            renderBullet(<InstructionBulletText i18nKey={i18nKey} />, index)
          ))
        )}
      </ul>
    </InstructionNotice>
  );
}

function ReviewSection({ title, children, readOnlyLabel }) {
  return (
    <section className="overflow-hidden rounded-xl border border-edge bg-surface shadow-sm">
      <header className="flex items-center justify-between gap-2 border-b border-edge bg-surface-muted/50 px-3 py-2">
        <h3 className="text-sm font-bold text-ink">{title}</h3>
        <span className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
          <FaLock className="h-2.5 w-2.5" aria-hidden />
          {readOnlyLabel}
        </span>
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function ReviewField({ label, value, className = '' }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-medium text-ink-subtle">{label}</p>
      <p className="mt-0.5 text-sm font-medium leading-relaxed text-ink">{value || '—'}</p>
    </div>
  );
}

function ScoreGradeTile({ code, score, t }) {
  return (
    <div className="flex min-w-0 flex-col items-center rounded-xl border border-edge/80 bg-gradient-to-b from-surface to-surface-muted/50 px-1.5 py-2 text-center shadow-sm">
      <span className="truncate text-[10px] font-bold uppercase tracking-wider text-link">
        {getSubjectShortLabel(t, code)}
      </span>
      <span className="mt-1 whitespace-nowrap text-lg font-bold tabular-nums leading-none text-ink">
        {score || '—'}
      </span>
    </div>
  );
}

function ScoreExtraReviewRow({ label, primary, secondary }) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
        {label}
      </p>
      <div className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-edge/80 bg-gradient-to-b from-surface to-surface-muted/50 px-3 py-2.5 shadow-sm">
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-link">
          {primary || '—'}
        </span>
        <span className="shrink-0 whitespace-nowrap text-lg font-bold tabular-nums leading-none text-ink">
          {secondary || '—'}
        </span>
      </div>
    </div>
  );
}

function ScoreReviewDisplay({ scoreRows, scoreExtras, t }) {
  const scoreByCode = Object.fromEntries(
    scoreRows.filter((row) => row.subjectCode).map((row) => [row.subjectCode, row.score])
  );
  const coreRows = ['CHI', 'ENG', 'MAT', 'CSD'].map((code) => ({
    subjectCode: code,
    score: scoreByCode[code] || '',
  }));
  const electiveRows = scoreRows.filter(
    (row) => row.subjectCode && !['CHI', 'ENG', 'MAT', 'CSD'].includes(row.subjectCode)
  );
  const hasScores = scoreRows.some((row) => row.subjectCode && row.score);

  if (!hasScores && !scoreExtras.categoryBCActive && !scoreExtras.spnBonusActive) {
    return <p className="text-sm text-ink-subtle">{t('jupasListSubmission.submit.emptyScores')}</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
          {t('jupasListSubmission.submit.coreHeading')}
        </p>
        <div className="grid grid-cols-4 gap-1.5">
          {coreRows.map((row) => (
            <ScoreGradeTile key={row.subjectCode} code={row.subjectCode} score={row.score} t={t} />
          ))}
        </div>
      </div>

      {electiveRows.length > 0 ? (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            {t('jupasListSubmission.submit.electivesHeading')}
          </p>
          <div className="grid grid-cols-4 gap-1.5">
            {electiveRows.map((row) => (
              <ScoreGradeTile key={row.subjectCode} code={row.subjectCode} score={row.score} t={t} />
            ))}
          </div>
        </div>
      ) : null}

      {scoreExtras.categoryBCActive || scoreExtras.spnBonusActive ? (
        <div className="space-y-4 border-t border-edge pt-3">
          {scoreExtras.categoryBCActive ? (
            <ScoreExtraReviewRow
              label={t('jupasListSubmission.submit.categoryBCHeading')}
              primary={scoreExtras.categoryBCSubject}
              secondary={scoreExtras.categoryBCScore}
            />
          ) : null}
          {scoreExtras.spnBonusActive ? (
            <ScoreExtraReviewRow
              label={t('jupasListSubmission.submit.spnBonusHeading')}
              primary={scoreExtras.spnBonusScheme}
              secondary={scoreExtras.spnBonusScore}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ReviewStep({
  scoreRows,
  scoreExtras,
  ranking,
  commentForm,
  language,
  t,
  submitError,
}) {
  const formatYesNo = (value) => {
    if (value === 'yes') return t('jupasListSubmission.comment.yes');
    if (value === 'no') return t('jupasListSubmission.comment.no');
    return '—';
  };

  return (
    <section aria-labelledby="jupas-survey-review-title" className="mx-auto w-full max-w-xl space-y-3">
      <h2 id="jupas-survey-review-title" className="sr-only">
        {t('jupasListSubmission.stepReview')}
      </h2>

      <InstructionNotice
        icon={FaClipboardCheck}
        title={t('jupasListSubmission.submit.reviewTitle')}
        titleId="jupas-survey-review-notice"
        tone="amber"
        className=""
      >
        <p className={`mt-0.5 text-xs leading-relaxed ${INSTRUCTION_TONE_STYLES.amber.body}`}>
          {t('jupasListSubmission.submit.reviewBody')}
        </p>
      </InstructionNotice>

      {submitError ? (
        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {submitError}
        </div>
      ) : null}

      <ReviewSection title={t('jupasListSubmission.stepScore')} readOnlyLabel={t('jupasListSubmission.submit.readOnly')}>
        <ScoreReviewDisplay scoreRows={scoreRows} scoreExtras={scoreExtras} t={t} />
      </ReviewSection>

      <ReviewSection title={t('jupasListSubmission.stepRanking')} readOnlyLabel={t('jupasListSubmission.submit.readOnly')}>
        {ranking.length > 0 ? (
          <ol className="space-y-1.5">
            {ranking.map((programme, index) => {
              const isAdmitted = index === ranking.length - 1;
              return (
                <li
                  key={programme.code}
                  className="flex select-none items-stretch gap-2 rounded-lg border border-edge bg-surface-muted/30 px-2 py-1.5"
                >
                  <span
                    className="flex h-6 w-6 shrink-0 self-center items-center justify-center rounded-full text-[9px] font-bold leading-none text-neutral-900"
                    style={{ backgroundColor: getJupasOrderBandColor(index) }}
                  >
                    {getJupasOrderRankLabel(index)}
                  </span>
                  <ProgrammeIdentity
                    programme={programme}
                    language={language}
                    admittedLabel={isAdmitted ? t('jupasListSubmission.admittedLabel') : null}
                    admittedLabelLayout="stacked"
                  />
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-sm text-ink-subtle">{t('jupasListSubmission.emptyRanking')}</p>
        )}
      </ReviewSection>

      <ReviewSection title={t('jupasListSubmission.stepComment')} readOnlyLabel={t('jupasListSubmission.submit.readOnly')}>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <ReviewField
              label={t('jupasListSubmission.comment.usedCalculator')}
              value={formatYesNo(commentForm.usedCalculator)}
            />
            {commentForm.usedCalculator === 'yes' ? (
              <ReviewField
                label={t('jupasListSubmission.comment.calculatorHelpfulness')}
                value={commentForm.calculatorHelpfulness || '—'}
              />
            ) : null}
            <ReviewField
              label={t('jupasListSubmission.comment.usedInterestTest')}
              value={formatYesNo(commentForm.usedInterestTest)}
            />
            {commentForm.usedInterestTest === 'yes' ? (
              <>
                <ReviewField
                  label={t('jupasListSubmission.comment.interestHelpfulness')}
                  value={commentForm.interestHelpfulness || '—'}
                />
                {requiresInterestLowHelpReason(commentForm.interestHelpfulness) ? (
                  <ReviewField
                    className="sm:col-span-2"
                    label={t('jupasListSubmission.comment.interestLowHelpReason')}
                    value={
                      commentForm.interestLowHelpReason
                        ? [
                            t(`jupasListSubmission.comment.interestLowHelpReasons.${commentForm.interestLowHelpReason}`),
                            commentForm.interestLowHelpReason === 'other'
                              ? commentForm.interestLowHelpReasonOther
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' — ')
                        : '—'
                    }
                  />
                ) : null}
                <ReviewField
                  label={t('jupasListSubmission.comment.interestAccuracy')}
                  value={commentForm.interestAccuracy || '—'}
                />
              </>
            ) : null}
          </div>
          <ReviewField
            label={t('jupasListSubmission.comment.improvements')}
            value={commentForm.improvements}
          />
          <ReviewField
            label={t('jupasListSubmission.comment.freeform')}
            value={commentForm.freeformFeedback}
          />
          {commentForm.volunteerWilling ? (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-2.5">
              <p className="text-xs font-semibold text-primary">{t('jupasListSubmission.comment.volunteerWilling')}</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <ReviewField label={t('jupasListSubmission.comment.volunteerName')} value={commentForm.volunteerName} />
                <ReviewField
                  label={t('jupasListSubmission.comment.contactDetails')}
                  value={
                    commentForm.volunteerContactMethod
                      ? `${t(`jupasListSubmission.comment.contactMethods.${commentForm.volunteerContactMethod}`)} · ${commentForm.volunteerContact}`
                      : commentForm.volunteerContact
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      </ReviewSection>
    </section>
  );
}

function CommentStep({ form, setForm, t }) {
  const updateField = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const compactInputClass = 'h-9 w-full rounded-lg border border-edge-strong bg-surface-muted px-3 text-base font-normal text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring';
  const compactTextareaClass = 'min-h-20 w-full resize-y rounded-lg border border-edge-strong bg-surface-muted px-3 py-2 text-base font-normal leading-relaxed text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring';
  const showInterestLowHelpReason = form.usedInterestTest === 'yes'
    && requiresInterestLowHelpReason(form.interestHelpfulness);

  return (
    <section aria-labelledby="jupas-survey-comment-title" className="mx-auto w-full max-w-xl space-y-2.5">
      <h2 id="jupas-survey-comment-title" className="sr-only">
        {t('jupasListSubmission.stepComment')}
      </h2>

      <section className="overflow-hidden rounded-xl border border-edge bg-surface shadow-sm">
        <header className="flex items-center gap-2.5 border-b border-edge bg-surface-muted/60 px-3 py-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FaCommentDots className="h-3.5 w-3.5" aria-hidden />
          </span>
          <h3 className="text-base font-bold text-ink">{t('jupasListSubmission.comment.feedbackTitle')}</h3>
        </header>

        <div className="space-y-2.5 p-3">
          <div className="rounded-lg border border-edge bg-surface-muted/35 p-2.5">
            <CommentFieldLabel required>{t('jupasListSubmission.comment.usedCalculator')}</CommentFieldLabel>
            <YesNoPills
              value={form.usedCalculator}
              onChange={(value) => updateField('usedCalculator', value)}
              t={t}
            />
            {form.usedCalculator === 'yes' ? (
              <div className="mt-2.5 border-t border-edge pt-2.5">
                <CommentFieldLabel required>{t('jupasListSubmission.comment.calculatorHelpfulness')}</CommentFieldLabel>
                <FeedbackScale
                  value={form.calculatorHelpfulness}
                  onChange={(value) => updateField('calculatorHelpfulness', value)}
                  lowLabel={t('jupasSurvey.scale.low')}
                  highLabel={t('jupasSurvey.scale.high')}
                  ariaLabel={t('jupasListSubmission.comment.calculatorHelpfulness')}
                />
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-edge bg-surface-muted/35 p-2.5">
            <CommentFieldLabel required>{t('jupasListSubmission.comment.usedInterestTest')}</CommentFieldLabel>
            <YesNoPills
              value={form.usedInterestTest}
              onChange={(value) => setForm((current) => ({
                ...current,
                usedInterestTest: value,
                ...(value !== 'yes'
                  ? {
                      interestHelpfulness: '',
                      interestLowHelpReason: '',
                      interestLowHelpReasonOther: '',
                      interestAccuracy: '',
                    }
                  : {}),
              }))}
              t={t}
            />
            {form.usedInterestTest === 'yes' ? (
              <div className="mt-2.5 space-y-2.5 border-t border-edge pt-2.5">
                <div>
                  <CommentFieldLabel required>{t('jupasListSubmission.comment.interestHelpfulness')}</CommentFieldLabel>
                  <FeedbackScale
                    value={form.interestHelpfulness}
                    onChange={(value) => setForm((current) => ({
                      ...current,
                      interestHelpfulness: value,
                      ...(Number(value) > 3
                        ? { interestLowHelpReason: '', interestLowHelpReasonOther: '' }
                        : {}),
                    }))}
                    lowLabel={t('jupasSurvey.scale.low')}
                    highLabel={t('jupasSurvey.scale.high')}
                    ariaLabel={t('jupasListSubmission.comment.interestHelpfulness')}
                  />
                </div>
                {showInterestLowHelpReason ? (
                  <InterestLowHelpReasonSelector
                    value={form.interestLowHelpReason}
                    otherValue={form.interestLowHelpReasonOther}
                    onChange={(value) => updateField('interestLowHelpReason', value)}
                    onOtherChange={(value) => updateField('interestLowHelpReasonOther', value)}
                    t={t}
                  />
                ) : null}
                <div>
                  <CommentFieldLabel required>{t('jupasListSubmission.comment.interestAccuracy')}</CommentFieldLabel>
                  <FeedbackScale
                    value={form.interestAccuracy}
                    onChange={(value) => updateField('interestAccuracy', value)}
                    lowLabel={t('jupasSurvey.scale.accuracyLow')}
                    highLabel={t('jupasSurvey.scale.accuracyHigh')}
                    ariaLabel={t('jupasListSubmission.comment.interestAccuracy')}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <label className="block">
            <CommentFieldLabel>{t('jupasListSubmission.comment.improvements')}</CommentFieldLabel>
            <textarea
              value={form.improvements}
              onChange={(event) => updateField('improvements', event.target.value)}
              className={`${compactTextareaClass} mt-1.5`}
              rows={3}
              maxLength={4000}
            />
          </label>

          <label className="block">
            <CommentFieldLabel>{t('jupasListSubmission.comment.freeform')}</CommentFieldLabel>
            <textarea
              value={form.freeformFeedback}
              onChange={(event) => updateField('freeformFeedback', event.target.value)}
              className={`${compactTextareaClass} mt-1.5`}
              rows={3}
              maxLength={4000}
            />
          </label>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-edge bg-surface shadow-sm">
        <header className="flex items-center gap-2.5 border-b border-edge bg-primary/5 px-3 py-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FaHandsHelping className="h-3.5 w-3.5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-ink">{t('jupasListSubmission.comment.volunteerTitle')}</h3>
            <p className="mt-0.5 text-base leading-relaxed text-ink-muted">{t('jupasListSubmission.comment.volunteerBody')}</p>
          </div>
        </header>

        <div className="p-3">
          <button
            type="button"
            onClick={() => updateField('volunteerWilling', !form.volunteerWilling)}
            aria-pressed={form.volunteerWilling}
            className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-all ${
              form.volunteerWilling
                ? 'border-primary bg-primary/10 text-ink'
                : 'border-edge-strong bg-surface-muted text-ink-muted hover:bg-surface'
            }`}
          >
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
              form.volunteerWilling
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-edge-strong bg-surface'
            }`}>
              {form.volunteerWilling ? <FaCheck className="h-2.5 w-2.5" aria-hidden /> : null}
            </span>
            <span className="text-base font-semibold">{t('jupasListSubmission.comment.volunteerWilling')}</span>
          </button>

          {form.volunteerWilling ? (
            <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
              <label className="block">
                <CommentFieldLabel required>{t('jupasListSubmission.comment.volunteerName')}</CommentFieldLabel>
                <input
                  type="text"
                  value={form.volunteerName}
                  onChange={(event) => updateField('volunteerName', event.target.value)}
                  className={`${compactInputClass} mt-1.5`}
                  maxLength={120}
                  autoComplete="name"
                />
              </label>
              <label className="block">
                <CommentFieldLabel required>{t('jupasListSubmission.comment.contactMethod')}</CommentFieldLabel>
                <select
                  value={form.volunteerContactMethod}
                  onChange={(event) => {
                    updateField('volunteerContactMethod', event.target.value);
                    updateField('volunteerContact', '');
                  }}
                  className={`${compactInputClass} mt-1.5 cursor-pointer`}
                >
                  <option value="">{t('jupasListSubmission.comment.contactMethodPlaceholder')}</option>
                  {['instagram', 'whatsapp', 'wechat', 'other'].map((method) => (
                    <option key={method} value={method}>
                      {t(`jupasListSubmission.comment.contactMethods.${method}`)}
                    </option>
                  ))}
                </select>
              </label>
              {form.volunteerContactMethod ? (
              <label className="block sm:col-span-2">
                <CommentFieldLabel required>{t('jupasListSubmission.comment.contactDetails')}</CommentFieldLabel>
                <input
                  type="text"
                  value={form.volunteerContact}
                  onChange={(event) => updateField('volunteerContact', event.target.value)}
                  className={`${compactInputClass} mt-1.5`}
                  placeholder={t(`jupasListSubmission.comment.contactHints.${form.volunteerContactMethod}`)}
                  maxLength={300}
                  autoComplete={form.volunteerContactMethod === 'whatsapp' ? 'tel' : 'off'}
                />
              </label>
              ) : null}
            </div>
          ) : null}

          <p className="mt-2.5 text-base leading-relaxed text-ink-subtle">{t('jupasListSubmission.comment.privacy')}</p>
        </div>
      </section>
    </section>
  );
}

export default function JupasSurveySubmission() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const surveyDraft = useMemo(() => readSurveyDraft(), []);
  const [collection, setCollection] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [ranking, setRanking] = useState(readStoredRanking);
  const [scoreRows, setScoreRows] = useState(surveyDraft.scores);
  const [scoreExtras, setScoreExtras] = useState(surveyDraft.extras);
  const [scoreInputKey, setScoreInputKey] = useState(0);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncConflicts, setSyncConflicts] = useState([]);
  const [pendingAccountMap, setPendingAccountMap] = useState(null);
  const accountSyncRef = useRef({ inFlight: false, lastUserId: null });
  const scoreRowsRef = useRef(scoreRows);
  scoreRowsRef.current = scoreRows;
  const rankingRef = useRef(ranking);
  rankingRef.current = ranking;
  const [loading, setLoading] = useState(false);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [error, setError] = useState('');
  const [recommendationsError, setRecommendationsError] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const [rankingPage, setRankingPage] = useState(1);
  const [commentForm, setCommentForm] = useState(surveyDraft.comment);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [admittedConfirmOpen, setAdmittedConfirmOpen] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);
  const [candidateConfirmOpen, setCandidateConfirmOpen] = useState(
    () => !hasJupasSurveyCandidateConfirmation()
  );
  const requestedStep = searchParams.get('step');
  const normalizedStep = requestedStep === 'submit' ? 'review' : requestedStep;
  const activeStep = ['ranking', 'comment', 'review'].includes(normalizedStep) ? normalizedStep : 'score';

  const stepAccess = useMemo(
    () => getSurveyStepAccess({ scoreRows, scoreExtras, ranking, commentForm }),
    [scoreRows, scoreExtras, ranking, commentForm]
  );

  const applyMergedScores = useCallback((mergedMap) => {
    const rows = mapToScoreRows(mergedMap);
    setScoreRows(rows);
    writeSurveyDraft({ scores: rows });
    setScoreInputKey((current) => current + 1);
  }, []);

  const applyScoreResolution = useCallback(async (resolution) => {
    if (!pendingAccountMap) {
      setSyncModalOpen(false);
      return;
    }
    const localMap = scoreRowsToMap(scoreRowsRef.current);
    const mergedMap = mergeScoreMaps(localMap, pendingAccountMap, resolution);
    applyMergedScores(mergedMap);
    await saveAccountScoreMap(mergedMap);
    setCommentForm((current) => {
      const next = mergeCommentFromUser(current, currentUser);
      writeSurveyDraft({ comment: next });
      return next;
    });
    setSyncModalOpen(false);
    setPendingAccountMap(null);
    setSyncConflicts([]);
    if (searchParams.get('prefill') === 'account') {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('prefill');
        return next;
      });
    }
  }, [applyMergedScores, currentUser, pendingAccountMap, searchParams, setSearchParams]);

  const runAccountSync = useCallback(async ({ forcePrompt = false } = {}) => {
    if (!isAuthenticated || accountSyncRef.current.inFlight) return;
    accountSyncRef.current.inFlight = true;
    try {
      const accountMap = await fetchAccountScoreMap();
      if (!accountMap) return;

      const localMap = scoreRowsToMap(scoreRowsRef.current);
      const conflicts = detectScoreConflicts(localMap, accountMap);
      const hasLocal = Object.keys(localMap).length > 0;
      const hasAccount = Object.keys(accountMap).length > 0;

      if (conflicts.length > 0) {
        setPendingAccountMap(accountMap);
        setSyncConflicts(conflicts);
        setSyncModalOpen(true);
        return;
      }

      const mergedMap = mergeScoreMaps(localMap, accountMap, 'fill-empty');
      if (JSON.stringify(mergedMap) !== JSON.stringify(localMap)) {
        applyMergedScores(mergedMap);
      }
      setCommentForm((current) => {
        const next = mergeCommentFromUser(current, currentUser);
        if (JSON.stringify(next) === JSON.stringify(current)) return current;
        writeSurveyDraft({ comment: next });
        return next;
      });
      if (searchParams.get('prefill') === 'account') {
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.delete('prefill');
          return next;
        });
      }
    } finally {
      accountSyncRef.current.inFlight = false;
    }
  }, [applyMergedScores, currentUser, isAuthenticated, searchParams, setSearchParams]);

  const syncRankingFromAccount = useCallback(async () => {
    if (!isAuthenticated || !collection) return;
    if (!shouldFillRankingFromAccount(rankingRef.current)) return;

    let programmeOrder = currentUser?.jupas_programme_order;
    if (!programmeOrder || !Object.keys(programmeOrder).length) {
      programmeOrder = await fetchAccountProgrammeOrder();
    }
    if (!programmeOrder || !Object.keys(programmeOrder).length) return;

    const programmes = await resolveRankingProgrammes(programmeOrder, collection);
    if (!programmes.length) return;

    setRanking(programmes.slice(0, MAX_PROGRAMMES));
  }, [collection, currentUser?.jupas_programme_order, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !collection) return;
    syncRankingFromAccount();
  }, [collection, currentUser?.User_ID, isAuthenticated, syncRankingFromAccount]);

  useEffect(() => {
    if (!isAuthenticated) {
      accountSyncRef.current.lastUserId = null;
      return;
    }
    const userId = currentUser?.User_ID || 'authenticated';
    if (accountSyncRef.current.lastUserId === userId) return;
    accountSyncRef.current.lastUserId = userId;
    runAccountSync({ forcePrompt: searchParams.get('prefill') === 'account' });
  }, [currentUser?.User_ID, isAuthenticated, runAccountSync, searchParams]);

  useEffect(() => {
    if (stepAccess[activeStep]) return;
    const fallback = getHighestAccessibleSurveyStep(stepAccess);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (fallback === 'score') next.delete('step');
      else next.set('step', fallback);
      return next;
    }, { replace: true });
  }, [activeStep, setSearchParams, stepAccess]);

  const applyStepChange = useCallback((step) => {
    if (submitError) setSubmitError('');
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (step === 'score') next.delete('step');
      else next.set('step', step);
      return next;
    });
  }, [setSearchParams, submitError]);

  const handleStepChange = useCallback((step) => {
    if (!SURVEY_FLOW_STEPS.includes(step)) return;
    if (!stepAccess[step]) return;

    const rankingIndex = SURVEY_FLOW_STEPS.indexOf('ranking');
    const targetIndex = SURVEY_FLOW_STEPS.indexOf(step);
    const leavingRankingForward = activeStep === 'ranking' && targetIndex > rankingIndex;

    if (leavingRankingForward && ranking.length > 0) {
      setPendingStep(step);
      setAdmittedConfirmOpen(true);
      return;
    }

    applyStepChange(step);
  }, [activeStep, applyStepChange, ranking.length, stepAccess]);

  const confirmAdmittedChoice = useCallback(() => {
    setAdmittedConfirmOpen(false);
    if (pendingStep) {
      applyStepChange(pendingStep);
      setPendingStep(null);
    }
  }, [applyStepChange, pendingStep]);

  const cancelAdmittedChoice = useCallback(() => {
    setAdmittedConfirmOpen(false);
    setPendingStep(null);
  }, []);

  const admittedProgramme = ranking.length > 0 ? ranking[ranking.length - 1] : null;
  const admittedRankIndex = Math.max(0, ranking.length - 1);

  const handleSubmit = useCallback(async () => {
    const validationError = validateJupasSurveySubmission({
      scoreRows,
      scoreExtras,
      ranking,
      commentForm,
    });
    if (validationError) {
      setSubmitError(t(`jupasListSubmission.errors.${validationError}`));
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = buildJupasSurveyPayload({
        scoreRows,
        scoreExtras,
        ranking,
        commentForm,
        language: i18n.language,
      });
      const response = await apiClient.post('/api/jupas-survey', payload);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t('jupasListSubmission.errors.submitFailed'));
      }

      clearSurveyStorage();
      navigate('/jupas-survey/success', {
        replace: true,
        state: { anonymous: Boolean(data.anonymous) },
      });
    } catch (error) {
      setSubmitError(error.message || t('jupasListSubmission.errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [commentForm, i18n.language, navigate, ranking, scoreExtras, scoreRows, t]);

  const handleScoresChange = useCallback((rows) => {
    setScoreRows(rows);
    writeSurveyDraft({ scores: rows });
  }, []);

  const handleSurveyExtrasChange = useCallback((extras) => {
    setScoreExtras(extras);
    writeSurveyDraft({ extras });
  }, []);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedCardStep, setDraggedCardStep] = useState(0);
  const searchRunRef = useRef(0);
  const rankingListRef = useRef(null);
  const dragSessionRef = useRef(null);
  const dragPreviewRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    resolveProgrammeListingCollection().then((resolvedCollection) => {
      if (cancelled) return;
      setCollection(resolvedCollection);
      if (!resolvedCollection) setError(t('jupasListSubmission.collectionError'));
    });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!collection) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setRecommendationsLoading(true);
        setRecommendationsError('');
        const response = await programmeService.getAllProgrammes(collection, { summary: true });
        if (!response.success) throw new Error('Recommendations failed');
        const programmes = [...(response.data || [])].sort((a, b) => {
          const offerDifference = getLatestOfferTotal(b) - getLatestOfferTotal(a);
          return offerDifference || compareProgrammeCodes(a, b);
        });
        if (!cancelled) setRecommendations(programmes);
      } catch (_recommendationsError) {
        if (!cancelled) {
          setRecommendations([]);
          setRecommendationsError(t('jupasListSubmission.recommendationsError'));
        }
      } finally {
        if (!cancelled) setRecommendationsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collection, t]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ranking));
    if (isAuthenticated && ranking.length > 0) {
      saveAccountProgrammeOrder(ranking);
    }
  }, [isAuthenticated, ranking]);

  useEffect(() => {
    writeSurveyDraft({ comment: commentForm });
  }, [commentForm]);

  useEffect(() => {
    const trimmedQuery = query.trim();
    const parsedQuery = extractInstitutionAliasQuery(trimmedQuery);
    const run = searchRunRef.current + 1;
    searchRunRef.current = run;

    if (!trimmedQuery || !collection) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const timeout = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const response = await programmeService.searchProgrammes(
          parsedQuery.query,
          parsedQuery.institution ? [parsedQuery.institution] : null,
          collection
        );
        if (searchRunRef.current !== run) return;
        if (!response.success) throw new Error('Search failed');
        setResults(Array.isArray(response.data) ? response.data : []);
      } catch (_searchError) {
        if (searchRunRef.current !== run) return;
        setResults([]);
        setError(t('jupasListSubmission.searchError'));
      } finally {
        if (searchRunRef.current === run) setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [collection, query, t]);

  const selectedCodes = useMemo(() => new Set(ranking.map((programme) => programme.code)), [ranking]);
  const searchActive = query.trim().length > 0;
  const visibleProgrammes = searchActive ? results : recommendations;
  const displayedProgrammes = useMemo(
    () => visibleProgrammes.filter((programme) => !selectedCodes.has(programme.code)),
    [selectedCodes, visibleProgrammes]
  );
  const searchTotalPages = Math.max(1, Math.ceil(displayedProgrammes.length / PAGE_SIZE));
  const rankingTotalPages = Math.max(1, Math.ceil(ranking.length / PAGE_SIZE));
  const pagedProgrammes = displayedProgrammes.slice(
    (searchPage - 1) * PAGE_SIZE,
    searchPage * PAGE_SIZE
  );
  const rankingPageOffset = (rankingPage - 1) * PAGE_SIZE;
  const pagedRanking = ranking.slice(rankingPageOffset, rankingPageOffset + PAGE_SIZE);
  const visibleError = searchActive ? error : recommendationsError;
  const listLoading = searchActive ? loading : recommendationsLoading;

  useEffect(() => {
    setSearchPage(1);
  }, [query]);

  useEffect(() => {
    setSearchPage((current) => Math.min(current, searchTotalPages));
  }, [searchTotalPages]);

  useEffect(() => {
    setRankingPage((current) => Math.min(current, rankingTotalPages));
  }, [rankingTotalPages]);

  const addProgramme = (programme) => {
    setRankingPage(Math.ceil(Math.min(MAX_PROGRAMMES, ranking.length + 1) / PAGE_SIZE));
    setRanking((current) => {
      if (current.length >= MAX_PROGRAMMES || current.some((item) => item.code === programme.code)) return current;
      return [...current, programme];
    });
  };

  const removeProgramme = (code) => {
    setRanking((current) => current.filter((programme) => programme.code !== code));
  };

  const clearDragState = () => {
    dragPreviewRef.current?.element.remove();
    dragPreviewRef.current = null;
    const session = dragSessionRef.current;
    if (session?.previousUserSelect !== undefined) {
      document.body.style.userSelect = session.previousUserSelect;
    }
    dragSessionRef.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDraggedCardStep(0);
  };

  useEffect(() => () => {
    dragPreviewRef.current?.element.remove();
    if (dragSessionRef.current?.previousUserSelect !== undefined) {
      document.body.style.userSelect = dragSessionRef.current.previousUserSelect;
    }
  }, []);

  const startPointerDrag = (event, session) => {
    const source = event.currentTarget;
    const preview = source.cloneNode(true);
    preview.setAttribute('aria-hidden', 'true');
    preview.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    Object.assign(preview.style, {
      position: 'fixed',
      top: `${session.rect.top}px`,
      left: `${session.rect.left}px`,
      width: `${session.rect.width}px`,
      height: `${session.rect.height}px`,
      margin: '0',
      opacity: '0.96',
      pointerEvents: 'none',
      transform: 'scale(0.99)',
      transition: 'none',
      zIndex: '10000',
      boxShadow: '0 14px 32px rgba(15, 23, 42, 0.18)',
    });
    document.body.appendChild(preview);
    dragPreviewRef.current = {
      element: preview,
      left: session.rect.left,
      pointerOffsetY: session.pointerOffsetY,
    };
    session.active = true;
    session.previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    setDraggedIndex(session.index);
    setDragOverIndex(session.index);
    const adjacentCard = session.cardCenters.find((card) => card.index === session.index + 1)
      || session.cardCenters.find((card) => card.index === session.index - 1);
    const sourceCenter = session.cardCenters.find((card) => card.index === session.index)?.centerY;
    setDraggedCardStep(
      adjacentCard && Number.isFinite(sourceCenter)
        ? Math.abs(adjacentCard.centerY - sourceCenter)
        : session.rect.height
    );
  };

  const handleRankingPointerDown = (event, index) => {
    if (event.button !== 0 || event.target.closest('button')) return;
    const source = event.currentTarget;
    const rect = source.getBoundingClientRect();
    source.setPointerCapture?.(event.pointerId);
    dragSessionRef.current = {
      active: false,
      index,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      pointerOffsetY: event.clientY - rect.top,
      rect,
      cardCenters: Array.from(rankingListRef.current?.children || []).map((card, cardIndex) => {
        const cardRect = card.getBoundingClientRect();
        return { index: rankingPageOffset + cardIndex, centerY: cardRect.top + cardRect.height / 2 };
      }),
    };
  };

  const handleRankingPointerMove = (event) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (!session.active) {
      const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
      if (distance < 5) return;
      startPointerDrag(event, session);
    }

    event.preventDefault();
    if (dragPreviewRef.current) {
      dragPreviewRef.current.element.style.left = `${dragPreviewRef.current.left}px`;
      dragPreviewRef.current.element.style.top = `${event.clientY - dragPreviewRef.current.pointerOffsetY}px`;
    }
    const closestCard = session.cardCenters.reduce((closest, card) => (
      Math.abs(card.centerY - event.clientY) < Math.abs(closest.centerY - event.clientY)
        ? card
        : closest
    ));
    session.overIndex = closestCard.index;
    setDragOverIndex(closestCard.index);
  };

  const handleRankingPointerUp = (event) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!session.active) {
      clearDragState();
      return;
    }

    const dropIndex = session.overIndex ?? session.index;
    if (session.index !== dropIndex) {
      setRanking((current) => {
        const next = [...current];
        const [programme] = next.splice(session.index, 1);
        next.splice(dropIndex, 0, programme);
        return next;
      });
    }
    clearDragState();
  };

  return (
    <div className="jupas-survey-submission mx-auto flex w-full max-w-7xl min-w-0 flex-col px-2 py-3 lg:max-w-3xl">
      <JupasSurveyCandidateConfirmModal
        open={candidateConfirmOpen}
        onConfirm={() => {
          setJupasSurveyCandidateConfirmed();
          setCandidateConfirmOpen(false);
        }}
        onExit={() => navigate('/jupas-survey')}
      />
      <JupasSurveySyncModal
        open={syncModalOpen}
        conflicts={syncConflicts}
        onChooseLocal={() => applyScoreResolution('local')}
        onChooseAccount={() => applyScoreResolution('account')}
      />
      <JupasSurveyAdmittedConfirmModal
        open={admittedConfirmOpen}
        programme={admittedProgramme}
        rankIndex={admittedRankIndex}
        language={i18n.language}
        onConfirm={confirmAdmittedChoice}
        onCancel={cancelAdmittedChoice}
      />
      <h1 className="sr-only">{t('navigation.jupasSurvey')}</h1>

      <SurveyStepNavigation
        activeStep={activeStep}
        onStepChange={handleStepChange}
        stepAccess={stepAccess}
        t={t}
      />

      {activeStep === 'review' ? (
        <ReviewStep
          scoreRows={scoreRows}
          scoreExtras={scoreExtras}
          ranking={ranking}
          commentForm={commentForm}
          language={i18n.language}
          t={t}
          submitError={submitError}
        />
      ) : activeStep === 'comment' ? (
        <CommentStep form={commentForm} setForm={setCommentForm} t={t} />
      ) : activeStep === 'ranking' ? (
        <>
      <StepInstructions stepKey="ranking" t={t} />

      <div className="relative mb-2">
        <label htmlFor="jupas-survey-programme-search" className="sr-only">
          {t('jupasListSubmission.searchLabel')}
        </label>
        <input
          id="jupas-survey-programme-search"
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClick={(event) => event.currentTarget.select()}
          placeholder={t('jupasListSubmission.searchPlaceholder')}
          autoComplete="off"
          className="h-9 w-full rounded-lg border border-edge-strong bg-surface px-3 pr-9 text-base text-ink shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 md:text-xs"
        />
        {listLoading ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <LoadingSpinner size="small" />
          </span>
        ) : query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-lg leading-none text-ink-subtle hover:bg-surface-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('jupasListSubmission.clearSearch')}
          >
            ×
          </button>
        ) : null}
      </div>

      <div className="grid h-[31.5rem] grid-cols-2 gap-2">
        <section aria-labelledby="jupas-search-results-title" className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-edge bg-surface">
          <header className="flex h-8 shrink-0 items-center gap-1.5 border-b border-edge px-2 sm:h-9">
            <h2 id="jupas-search-results-title" className="min-w-0 flex-1 truncate text-[11px] font-bold text-ink">
              {t('jupasListSubmission.searchResults')}
            </h2>
            <FlowSwipeHint direction="right" t={t} />
          </header>

          <div className="min-h-0 flex-1 overflow-hidden px-1 pb-2 pt-1">
            {visibleError ? <p className="p-2 text-center text-xs text-red-600">{visibleError}</p> : null}
            {!visibleError && searchActive && !loading && results.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs leading-relaxed text-ink-subtle">
                {t('jupasListSubmission.noResults')}
              </p>
            ) : null}
            <div className="space-y-1">
              {pagedProgrammes.map((programme) => {
                const rankingFull = ranking.length >= MAX_PROGRAMMES;
                return (
                  <MobileProgrammeSwipeAction
                    key={programme.code}
                    enabled={!rankingFull}
                    tone="add"
                    direction="right"
                    actionLabel={t('jupa.add')}
                    actionLabelClassName="text-[11px]"
                    compactActionInset
                    neutralAtRest
                    onAction={() => addProgramme(programme)}
                    className="rounded-lg"
                  >
                    <div className="flex h-10 w-full min-w-0 select-none items-center gap-1 rounded-lg border border-edge bg-surface px-1.5 py-0.5 transition hover:border-primary/40 hover:bg-surface-muted">
                      <div className="flex min-w-0 flex-1 items-center text-left">
                        <ProgrammeIdentity programme={programme} language={i18n.language} preferShortEnglishName />
                      </div>
                      <button
                        type="button"
                        disabled={rankingFull}
                        onClick={() => addProgramme(programme)}
                        className={`hidden md:inline-flex ${JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS} ${JUPAS_VERY_SAFE_ACTION_CLASS}`}
                        aria-label={t('jupasListSubmission.addProgramme', { code: programme.code })}
                        title={t('jupa.add')}
                      >
                        <span className={`${JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS} group-hover:bg-green-50 group-active:bg-green-100`}>
                          <FaPlusCircle className="h-4 w-4" aria-hidden />
                        </span>
                      </button>
                    </div>
                  </MobileProgrammeSwipeAction>
                );
              })}
            </div>
          </div>
          <ListPager
            page={searchPage}
            totalPages={searchTotalPages}
            onPageChange={setSearchPage}
            label={t('jupasListSubmission.searchPagination')}
            t={t}
          />
        </section>

        <section aria-labelledby="jupas-ranking-title" className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-edge bg-surface">
          <header className="flex h-8 shrink-0 items-center gap-1.5 border-b border-edge px-2 sm:h-9">
            <FlowSwipeHint direction="left" t={t} />
            <h2 id="jupas-ranking-title" className="min-w-0 flex-1 truncate text-right text-[11px] font-bold text-ink">
              {t('jupasListSubmission.ranking')}
            </h2>
          </header>

          <div className="min-h-0 flex-1 overflow-hidden px-1 pb-2 pt-1">
            {ranking.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs leading-relaxed text-ink-subtle">
                {t('jupasListSubmission.emptyRanking')}
              </p>
            ) : null}
            <div ref={rankingListRef} role="list" className="space-y-1">
              {pagedRanking.map((programme, pageIndex) => {
                const index = rankingPageOffset + pageIndex;
                const isAdmittedChoice = ranking.length > 0 && index === ranking.length - 1;
                const shouldShiftUp = draggedIndex !== null
                  && dragOverIndex !== null
                  && draggedIndex < dragOverIndex
                  && index > draggedIndex
                  && index <= dragOverIndex;
                const shouldShiftDown = draggedIndex !== null
                  && dragOverIndex !== null
                  && draggedIndex > dragOverIndex
                  && index >= dragOverIndex
                  && index < draggedIndex;
                const dragShiftY = shouldShiftUp
                  ? -draggedCardStep
                  : shouldShiftDown
                    ? draggedCardStep
                    : 0;

                return (
                <div
                  key={programme.code}
                  className="relative min-w-0"
                  style={{
                    transform: `translate3d(0, ${dragShiftY}px, 0)`,
                    transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
                    willChange: draggedIndex !== null ? 'transform' : undefined,
                    zIndex: dragShiftY !== 0 ? 1 : 0,
                  }}
                >
                <MobileProgrammeSwipeAction
                  tone="remove"
                  direction="left"
                  actionLabel={t('jupa.remove')}
                  actionLabelClassName="text-[11px]"
                  compactActionInset
                  neutralAtRest
                  onAction={() => removeProgramme(programme.code)}
                  onPointerDown={(event) => handleRankingPointerDown(event, index)}
                  onPointerMove={handleRankingPointerMove}
                  onPointerUp={handleRankingPointerUp}
                  onPointerCancel={clearDragState}
                  allowOverflow={draggedIndex === index}
                  className="rounded-lg"
                >
                <div
                  role="listitem"
                  className={`flex h-10 min-w-0 touch-none select-none items-center gap-1 rounded-lg border border-edge bg-surface px-1 py-0.5 shadow-sm md:cursor-grab md:active:cursor-grabbing ${draggedIndex === index ? 'opacity-0' : ''}`}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold leading-none text-neutral-900"
                    style={{ backgroundColor: getJupasOrderBandColor(index) }}
                  >
                    {getJupasOrderRankLabel(index)}
                  </span>
                  <ProgrammeIdentity
                    programme={programme}
                    language={i18n.language}
                    admittedLabel={isAdmittedChoice ? t('jupasListSubmission.admittedLabel') : null}
                    preferShortEnglishName
                  />
                  <button
                    type="button"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeProgramme(programme.code);
                    }}
                    className={`hidden md:inline-flex ${JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS} ${JUPAS_HIGH_RISK_ACTION_CLASS}`}
                    aria-label={t('jupasListSubmission.removeProgramme', { code: programme.code })}
                    title={t('jupa.remove')}
                  >
                    <span className={`${JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS} group-hover:bg-red-50 group-active:bg-red-100`}>
                      <FaMinusCircle className="h-4 w-4" aria-hidden />
                    </span>
                  </button>
                </div>
                </MobileProgrammeSwipeAction>
                </div>
                );
              })}
            </div>
          </div>
          <ListPager
            page={rankingPage}
            totalPages={rankingTotalPages}
            onPageChange={setRankingPage}
            label={t('jupasListSubmission.rankingPagination')}
            t={t}
          />
        </section>
      </div>
        </>
      ) : (
        <section aria-labelledby="jupas-survey-score-title" className="mx-auto w-full max-w-xl">
          <h2 id="jupas-survey-score-title" className="sr-only">
            {t('jupasListSubmission.stepScore')}
          </h2>
          <StepInstructions stepKey="score" t={t} />
          <DSEScoreInputer
            key={scoreInputKey}
            className="!mx-0"
            surveyCompact
            balancedActionGutter
            showCategoryBCRow
            showSpnBonusRow
            seedScores={scoreRows.length ? scoreRows : undefined}
            initialSurveyExtras={scoreExtras}
            onScoresChange={handleScoresChange}
            onSurveyExtrasChange={handleSurveyExtrasChange}
          />
        </section>
      )}
      <SurveyStepFooter
        activeStep={activeStep}
        onStepChange={handleStepChange}
        onBackHome={() => navigate('/jupas-survey')}
        onSubmit={handleSubmit}
        submitDisabled={submitting}
        stepAccess={stepAccess}
        t={t}
      />
    </div>
  );
}
